import { Dialog, Box } from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import type { ReactNode } from "react";
import { v2 } from "./v2Tokens";

type Props = {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: ReactNode;
  actions?: ReactNode;
  maxWidth?: string;
  /**
   * A strip between the header and the body — the mock puts mode tabs here, outside the
   * scrolling area, so switching mode never scrolls the tabs out of reach.
   */
  tabs?: ReactNode;
};

/** The dialog shell for Nutrition V2: header with a close ✕, a scrolling body, and a footer
 *  that lays its actions out as cancel-at-the-start / confirm-at-the-end. */
const V2Dialog = ({ open, onClose, title, subtitle, children, actions, maxWidth = "560px", tabs }: Props) => (
  <Dialog
    open={open}
    onClose={onClose}
    sx={{
      "& .MuiDialog-paper": {
        // `sm`, not `md`: between 600 and 900px there is plenty of room for a 560px dialog, and
        // jumping straight from xs to md left every dialog at 95% of a tablet.
        width: { xs: "100%", sm: maxWidth },
        maxWidth: { xs: "100%", sm: maxWidth },
        // A phone dialog earns the whole width. MUI's own 32px paper margins plus a 95% cap were
        // costing ~50px of a 375px screen before the dialog's padding had started.
        margin: { xs: "12px", sm: "32px" },
        maxHeight: { xs: "calc(100% - 24px)", sm: "calc(100% - 64px)" },
        borderRadius: "18px",
        boxShadow: v2.shadowDialog,
      },
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: "12px",
        padding: { xs: "16px 16px 12px", md: "20px 22px 16px" },
        borderBottom: `1px solid ${v2.lineSoft}`,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ fontSize: "19px", fontWeight: 600, letterSpacing: "-.02em", color: v2.ink }}>{title}</Box>
        {subtitle && (
          <Box sx={{ fontSize: "13px", color: v2.ink3, marginTop: "3px", fontVariantNumeric: "tabular-nums" }}>
            {subtitle}
          </Box>
        )}
      </Box>
      <Box
        component="button"
        type="button"
        aria-label="סגור"
        onClick={onClose}
        sx={{
          marginInlineStart: "auto",
          flex: "none",
          width: { xs: "40px", md: "32px" },
          height: { xs: "40px", md: "32px" },
          display: "grid",
          placeItems: "center",
          borderRadius: v2.rSm,
          border: "none",
          background: "transparent",
          color: v2.ink3,
          cursor: "pointer",
          transition: "background .12s, color .12s",
          "&:hover": { background: v2.lineSoft, color: v2.ink },
        }}
      >
        <CloseIcon sx={{ fontSize: 18 }} />
      </Box>
    </Box>

    {tabs && (
      <Box
        sx={{
          display: "flex",
          gap: "8px",
          // two or three nowrap pills do not fit 267px side by side
          flexWrap: { xs: "wrap", md: "nowrap" },
          padding: { xs: "12px 16px 4px", md: "16px 22px 4px" },
        }}
      >
        {tabs}
      </Box>
    )}

    <Box
      sx={{
        padding: { xs: "12px 16px 16px", md: "16px 22px 20px" },
        // `dvh` on a phone: `vh` is measured with mobile Safari's URL bar retracted, so a body
        // sized in `vh` runs past the bottom of the screen exactly when the bar is showing
        maxHeight: { xs: "60dvh", md: "60vh" },
        overflowY: "auto",
      }}
    >
      {children}
    </Box>

    {actions && (
      <Box
        sx={{
          padding: { xs: "12px 16px", md: "14px 22px" },
          borderTop: `1px solid ${v2.lineSoft}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          // Three nowrap buttons — "הישאר בתפריט", "שמור וצא", "צא בלי לשמור" — come to ~250px
          // against the ~223px a phone dialog's footer had. Without this they simply overflowed,
          // and the unsaved-changes guard is the last dialog that should be unusable.
          flexWrap: { xs: "wrap", md: "nowrap" },
          rowGap: "8px",
          gap: "8px",
        }}
      >
        {actions}
      </Box>
    )}
  </Dialog>
);

export default V2Dialog;
